import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { qk, useConfig, useDevices, useMeasurement, useMeasurementResults, useMeasurementStatus, useSamples } from '../api/queries';
import type { Cancellation, Device, Measurement, MeasurementStartIn } from '../api/types';
import { useAction } from '../components/feedback';
import { TempChart } from '../components/TempChart';
import { Async, Badge, Card, Field, KV, PageHeader } from '../components/ui';
import { byId, deviceName, isSensor } from '../lib/domain';
import { CANCELLATION_LABEL, FAILURE_LABEL, MEASUREMENT_STATUS_LABEL, OUTCOME_LABEL, STAGE_LABEL } from '../lib/labels';
import { ago, dayTime, duration, localInputToIso, readingFreshness, time } from '../lib/time';

export function MeasurementPage() {
  const status = useMeasurementStatus();
  const [selected, setSelected] = useState<string | null>(null);
  const current = status.data && (status.data.status === 'scheduled' || status.data.status === 'running') ? status.data : null;

  return (
    <>
      <PageHeader title="Mesure de déperdition" subtitle="Confort jusqu’à la cible haute, Hors-gel jusqu’à la basse, puis Confort. 10 h maximum, deux sondes en vérification croisée." />
      <div className="split">
        <div className="split-side stack-l">
          {current ? <CurrentTest m={current} /> : <ScheduleForm />}
        </div>
        <div className="split-main stack-l">
          <Results selected={selected} onSelect={setSelected} />
        </div>
      </div>
    </>
  );
}

function ScheduleForm() {
  const { run, busy } = useAction();
  const devices = useDevices();
  const sensors = (devices.data ?? []).filter(isSensor);
  const [at, setAt] = useState('');
  const [high, setHigh] = useState('21');
  const [low, setLow] = useState('17');
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    if (!ids.length && sensors.length >= 2) setIds(sensors.slice(0, 2).map((s) => s.id));
  }, [sensors, ids.length]);
  const ok = at && ids.length === 2 && +low < +high;

  return (
    <Card title="Programmer un test">
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        const iso = localInputToIso(at);
        if (!iso || !ok) return;
        const body: MeasurementStartIn = { scheduled_at: iso, high_temperature_c: +high, low_temperature_c: +low, sensor_ids: ids, max_duration_hours: 10 };
        run((confirmation) => api.send<Measurement>('POST', '/measurement/start', { body, confirmation }).then((r) => r.data), {
          success: (m) => `Test programmé le ${dayTime(m.scheduled_at)}`,
          invalidate: [['measurement'], ['wakes']],
        });
      }}>
        <Field label="Démarrage" htmlFor="m-at"><input id="m-at" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} required /></Field>
        <div className="grid-2">
          <Field label="Cible haute (°C)" htmlFor="m-hi"><input id="m-hi" type="number" step="0.5" value={high} onChange={(e) => setHigh(e.target.value)} /></Field>
          <Field label="Cible basse (°C)" htmlFor="m-lo" hint={+low >= +high ? 'Doit être inférieure à la haute' : undefined}><input id="m-lo" type="number" step="0.5" value={low} onChange={(e) => setLow(e.target.value)} /></Field>
        </div>
        <fieldset className="field">
          <legend className="label">Sondes (2 distinctes)</legend>
          {sensors.length < 2 && <p className="alert alert-warn small">Il faut deux sondes de température pour programmer un test.</p>}
          {sensors.map((s) => (
            <label key={s.id} className="check">
              <input type="checkbox" checked={ids.includes(s.id)} disabled={!ids.includes(s.id) && ids.length >= 2}
                onChange={(e) => setIds((xs) => (e.target.checked ? [...xs, s.id] : xs.filter((x) => x !== s.id)))} />
              {deviceName(s)} <SensorFreshness d={s} />
            </label>
          ))}
        </fieldset>
        <p className="small muted">Durée maximale : <span className="mono">10 h</span>. Si un radiateur est suspendu ou si un réveil tombe dans la fenêtre, un choix vous sera demandé.</p>
        <button className="btn btn-primary btn-block" disabled={busy || !ok}>Programmer</button>
      </form>
    </Card>
  );
}

function SensorFreshness({ d }: { d: Device }) {
  if (d.suspended) return <span className="small muted">suspendue</span>;
  const f = readingFreshness(d.last_reading?.measured_at);
  if (!d.last_reading) return <span className="small muted">aucune mesure</span>;
  return <span className={`small ${f === 'fresh' ? 'muted' : 'text-bad'}`}>{d.last_reading.temperature_c.toFixed(1)} °C · {ago(d.last_reading.measured_at)}</span>;
}

function CurrentTest({ m }: { m: Measurement }) {
  const { run, busy } = useAction();
  const devices = useDevices();
  const config = useConfig();
  const devMap = byId(devices.data);
  const samples = useSamples(m.status === 'running' ? m.id : null, true);
  const stability = config.data?.measurement_stability_minutes ?? 15;
  const order = ['initial_heating', 'cooling', 'reheating'] as const;
  const current = m.stage && m.stage !== 'completed' ? order.indexOf(m.stage) : -1;
  const s = MEASUREMENT_STATUS_LABEL[m.status];

  return (
    <Card title={m.status === 'running' ? 'Test en cours' : 'Test programmé'} actions={<Badge tone={s.tone}>{s.label}</Badge>}>
      <div className="stack">
        {m.status === 'scheduled' ? (
          <>
            <div className="mono big">{dayTime(m.scheduled_at)}</div>
            <p className="small muted">{m.config.high_temperature_c} → {m.config.low_temperature_c} → {m.config.high_temperature_c} °C · {(m.participant_device_ids ?? []).length} radiateur(s) participant(s)</p>
          </>
        ) : (
          <>
            <p className="small muted">Démarré {dayTime(m.started_at)} · fin au plus tard {time(m.deadline_at)}</p>
            <ol className="stepper" aria-label="Étapes">
              {order.map((st, i) => (
                <li key={st} className={i < current ? 'done' : i === current ? 'now' : ''} aria-current={i === current ? 'step' : undefined}>
                  <span className="bar" />
                  <span className="small strong">{i + 1} · {STAGE_LABEL[st]}</span>
                  <span className="xsmall muted">{i === current ? (m.stage_confirmed_at ? `depuis ${time(m.stage_confirmed_at)}` : 'confirmation des relais…') : i < current ? 'terminée' : 'à venir'}</span>
                </li>
              ))}
            </ol>
            <div className="grid-2">
              {m.config.sensor_ids.map((id) => {
                const r = m.last_readings?.[id];
                return (
                  <div key={id} className="panel">
                    <div className="small muted">{deviceName(devMap.get(id))}</div>
                    <div className="temp-xl">{r ? `${r.temperature_c.toFixed(1)}°` : '—'}</div>
                    <div className="xsmall muted">{r ? ago(r.measured_at) : 'aucune mesure'}</div>
                  </div>
                );
              })}
            </div>
            <KV items={[
              ['Écart entre sondes', <span className={`mono ${(m.temperature_gap_c ?? 0) > 2 ? 'text-bad' : ''}`}>{m.temperature_gap_c != null ? `${m.temperature_gap_c.toFixed(1)} °C / 2 °C max` : '—'}</span>],
              ['Stabilité sur la cible', <span className="mono">{m.stability_minutes ?? 0} / {stability} min</span>],
              ['Fenêtre de plateau complète', m.plateau_window_complete ? 'oui' : 'non'],
            ]} />
            {samples.data && <TempChart samples={samples.data.data} sensorIds={m.config.sensor_ids} stages={m.stages} high={m.config.high_temperature_c} low={m.config.low_temperature_c} names={Object.fromEntries((devices.data ?? []).map((d) => [d.id, deviceName(d)]))} />}
          </>
        )}
        <KV items={[
          ['Participants', (m.participant_device_ids ?? []).map((id) => deviceName(devMap.get(id))).join(', ') || '—'],
          ['Exclus (suspendus)', (m.excluded_device_ids ?? []).map((id) => deviceName(devMap.get(id))).join(', ') || 'aucun'],
        ]} />
        <p className="small muted">Pendant le test, seules l’annulation et la présence sont acceptées.</p>
        <button type="button" className="btn btn-danger btn-block" disabled={busy}
          onClick={() => {
            if (!window.confirm('Annuler le test ? La programmation normale reprend ; les forçages antérieurs ne reprennent pas.')) return;
            run(() => api.send<Cancellation>('POST', '/measurement/cancel'), { success: 'Test annulé', invalidate: [['measurement'], qk.state] });
          }}>
          Annuler le test
        </button>
      </div>
    </Card>
  );
}

function Results({ selected: chosen, onSelect }: { selected: string | null; onSelect: (id: string) => void }) {
  const list = useMeasurementResults();
  // Par défaut : le dernier test terminé, annulé ou échoué.
  const selected = chosen ?? list.data?.data.find((r) => r.status !== 'scheduled' && r.status !== 'running')?.id ?? null;
  const m = useMeasurement(selected);
  return (
    <>
      {selected && <ResultDetail m={m.data} loading={m.isLoading} error={m.error} />}
      <Card title="Tests">
        <Async q={{ data: list.data?.data, isLoading: list.isLoading, error: list.error }} empty={<p className="small muted">Aucun test réalisé.</p>}>
          {(rows) => (
            <ul className="pick-list">
              {rows.map((r) => (
                <li key={r.id}>
                  <button type="button" className={`pick ${r.id === selected ? 'is-on' : ''}`} onClick={() => onSelect(r.id)}>
                    <span className="row between"><span className="strong">{dayTime(r.scheduled_at)}</span><Badge tone={MEASUREMENT_STATUS_LABEL[r.status].tone}>{MEASUREMENT_STATUS_LABEL[r.status].label}</Badge></span>
                    <span className="small muted">{summary(r)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Async>
      </Card>
    </>
  );
}

function summary(m: Measurement): string {
  if (m.failure_message) return m.failure_message;
  if (m.failure_reason) return FAILURE_LABEL[m.failure_reason];
  if (m.cancellation_reason) return CANCELLATION_LABEL[m.cancellation_reason];
  if (m.outcome) return OUTCOME_LABEL[m.outcome];
  return `${m.config.high_temperature_c ?? '–'} / ${m.config.low_temperature_c ?? '–'} °C`;
}

function ResultDetail({ m, loading, error }: { m?: Measurement; loading: boolean; error: unknown }) {
  const devices = useDevices();
  const samples = useSamples(m?.id ?? null);
  const names = Object.fromEntries((devices.data ?? []).map((d) => [d.id, deviceName(d)]));
  return (
    <Card title={m ? `Test du ${dayTime(m.scheduled_at)}` : 'Test'} actions={m && <Badge tone={m.interrupted ? 'bad' : m.outcome === 'all_targets_reached' ? 'ok' : 'warn'}>{summary(m)}</Badge>}>
      <Async q={{ data: m, isLoading: loading, error }}>
        {(m) => (
          <div className="stack">
            {m.interrupted && (
              <p className="alert alert-bad small">
                Interrompu {m.interrupted_stage ? `pendant « ${STAGE_LABEL[m.interrupted_stage]} »` : ''} {m.ended_at ? `à ${time(m.ended_at)}` : ''} : {m.failure_message ?? (m.failure_reason ? FAILURE_LABEL[m.failure_reason] : '')}
                {m.affected_device_ids?.length ? ` · concerne ${m.affected_device_ids.map((id) => names[id] ?? id).join(', ')}` : ''}. Les durées partielles ne valent pas un protocole complet.
              </p>
            )}
            <Async q={{ data: samples.data?.data, isLoading: samples.isLoading, error: samples.error }} empty={<p className="small muted">Aucun échantillon.</p>}>
              {(rows) => <TempChart samples={rows} sensorIds={m.config.sensor_ids} stages={m.stages} high={m.config.high_temperature_c} low={m.config.low_temperature_c} names={names} />}
            </Async>
            {m.stages?.length ? (
              <div className="table-scroll">
                <table className="table">
                  <thead><tr><th scope="col">Étape</th><th scope="col">Durée</th><th scope="col">Fin</th>{m.config.sensor_ids.map((id) => <th key={id} scope="col">{names[id] ?? id}</th>)}</tr></thead>
                  <tbody>
                    {m.stages.map((s) => (
                      <tr key={s.stage}>
                        <th scope="row">{STAGE_LABEL[s.stage]}</th>
                        <td className="mono">{duration(s.duration_minutes)}</td>
                        <td>{s.end_cause === 'target_stable' ? 'Cible stabilisée' : s.end_cause === 'plateau' ? <span className="text-warn strong">Plateau</span> : '—'}</td>
                        {m.config.sensor_ids.map((id) => <td key={id} className="mono">{s.reached_temperatures?.[id] != null ? `${s.reached_temperatures[id].toFixed(1)} °C` : '—'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="xsmall muted">Durées comptées depuis la confirmation des relais par tous les radiateurs participants.</p>
              </div>
            ) : null}
          </div>
        )}
      </Async>
    </Card>
  );
}
